import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, ArrowLeft, CheckCircle2, ShieldCheck, Thermometer, Wind, Wrench, Sparkles, Clock, ArrowRight, Zap, MonitorSmartphone, Send } from 'lucide-react';
import Footer from '../components/Footer';
import HeaderSocials from '../components/HeaderSocials';
import InstagramVideoSection from '../components/InstagramVideoSection';
import LandingFaqSchema from '../components/LandingFaqSchema';
import useLandingContent from '../hooks/useLandingContent';

const ACCENT = 'from-orange-400 to-amber-500';
const GLOW = 'shadow-[0_0_80px_rgba(251,146,60,0.4)]';
const GRAD = `bg-gradient-to-r ${ACCENT} bg-clip-text text-transparent`;

const FEATURES = [
  'Продування корпусу, БП та всіх вентиляторів від пилу',
  'Демонтаж і чистка кулера процесора та радіатора',
  'Заміна термопасти на CPU (за потреби — на GPU)',
  'Чистка відеокарти від пилу, ревізія системи охолодження',
  'Перевірка та змазка підшипників вентиляторів',
  'Кабель-менеджмент і тест температур під навантаженням'
];

const STEPS = [
  { title: 'Безкоштовна діагностика', desc: 'Заміряємо температури CPU/GPU, слухаємо вентилятори, оглядаємо системний блок. Даємо чесний висновок.', img: '/images/generated/pc-step-1.png', icon: ShieldCheck },
  { title: 'Розбирання та чистка', desc: 'Знімаємо бічну панель, продуваємо корпус, БП, радіатори й вентилятори стисненим повітрям та щітками.', img: '/images/generated/pc-step-2.png', icon: Wind },
  { title: 'Заміна термопасти', desc: 'Знімаємо кулер, чистимо кришку процесора, наносимо преміум-термопасту. За потреби обслуговуємо відеокарту.', img: '/images/generated/pc-step-3.png', icon: Thermometer },
  { title: 'Тестування', desc: 'Збираємо, робимо кабель-менеджмент, ганяємо стрес-тест. Показуємо температури «до і після».', img: '/images/generated/pc-step-1.png', icon: Zap }
];

const SIGNS = [
  { icon: '🔥', text: 'ПК перегрівається та вимикається сам' },
  { icon: '🌀', text: 'Вентилятори гудуть як турбіна' },
  { icon: '🐢', text: 'Падає FPS в іграх, фризи й лаги' },
  { icon: '💨', text: 'З корпусу летить пил, забиті фільтри' },
  { icon: '🖥️', text: 'Артефакти на екрані, раптові перезавантаження' },
  { icon: '📅', text: 'Не чистили більше року' }
];

const FAQS = [
  { q: 'Скільки коштує чистка комп\'ютера?', a: 'Чистка стаціонарного ПК — від 500 грн. Ціна залежить від ступеня забруднення та потреби в обслуговуванні відеокарти. Точну суму назве майстер після діагностики.' },
  { q: 'Скільки триває чистка ПК?', a: 'Зазвичай 1–2 години. Можна зачекати або залишити на кілька годин і забрати того ж дня.' },
  { q: 'Яку термопасту використовуєте?', a: 'Преміум-класу — Arctic MX-6 або Thermal Grizzly, які значно ефективніші за стокову пасту й довше тримають температуру.' },
  { q: 'Мої дані та Windows у безпеці?', a: 'Так. Ми працюємо лише із «залізом» — накопичувачі, дані та система не чіпаються.' },
  { q: 'Даєте гарантію?', a: 'Так, офіційна гарантія 30 днів на виконані роботи. Діагностика — безкоштовна.' }
];

const PCCleaningPage = () => {
  const landing = useLandingContent('pc');

  return (
    <div className="min-h-screen console-page" data-testid="pc-cleaning-page">
      <Helmet>
        <title>Чистка комп'ютера в Одесі — стаціонарний ПК + термопаста | CIMM Computers</title>
        <meta name="description" content="Професійна чистка стаціонарних комп'ютерів в Одесі: продування від пилу, заміна термопасти, обслуговування системи охолодження CPU і GPU. Усуваємо перегрів, шум і фризи. Від 500 грн. Гарантія 30 днів. ☎ +38 097 417 8788" />
        <meta name="keywords" content="чистка комп'ютера Одеса, чистка ПК Одеса, чистка системного блоку Одеса, заміна термопасти комп'ютер Одеса, перегрів ПК, профілактика комп'ютера Одеса" />
        <link rel="canonical" href="https://cimm.od.ua/chistka-pk" />
        <meta property="og:title" content="Чистка комп'ютера в Одесі — CIMM Computers" />
        <meta property="og:description" content="Чистка ПК від пилу та заміна термопасти. Усуваємо перегрів і шум. Від 500 грн, гарантія 30 днів." />
        <meta property="og:image" content="https://cimm.od.ua/images/generated/pc-hero.png" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cimm.od.ua/chistka-pk" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: 'Чистка стаціонарного комп\'ютера та заміна термопасти',
          provider: {
            '@type': 'LocalBusiness',
            name: 'CIMM Computers',
            telephone: '+380974178788',
            address: { '@type': 'PostalAddress', addressLocality: 'Одеса', addressCountry: 'UA' }
          },
          areaServed: { '@type': 'City', name: 'Одеса' },
          offers: [{ '@type': 'Offer', name: 'Чистка комп\'ютера', price: '500', priceCurrency: 'UAH' }]
        })}</script>
      </Helmet>
      <LandingFaqSchema category="pc" />

      {/* Header */}
      <header className="cyber-header fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="header-home-link">
            <div className="text-2xl font-bold neon-text">CIMM</div>
            <div className="text-sm text-gray-400 hidden md:block">Комп'ютери та Ремонт</div>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-gray-300 hover:text-orange-400 transition-all">Головна</Link>
            <Link to="/laptops" className="text-gray-300 hover:text-orange-400 transition-all">Ноутбуки</Link>
            <Link to="/chistka-noutbukiv" className="text-gray-300 hover:text-orange-400 transition-all">Чистка ноутбуків</Link>
            <Link to="/chistka-konsoley" className="text-gray-300 hover:text-orange-400 transition-all">Чистка консолей</Link>
            <Link to="/contacts" className="text-gray-300 hover:text-orange-400 transition-all">Контакти</Link>
          </nav>
          <div className="flex items-center gap-2">
            <HeaderSocials />
            <a href="tel:+380974178788" className="neon-text font-semibold" data-testid="header-phone-link">+38 097 417 8788</a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[92vh] md:min-h-[88vh] flex items-center overflow-hidden pt-28 pb-16 md:pt-24 md:pb-20 console-hero">
        <div className="absolute inset-0 pointer-events-none console-hero-bg" aria-hidden="true">
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 90% at 75% 50%, rgba(251,146,60,0.2), transparent 65%)' }} />
        </div>

        <div className="absolute inset-y-0 right-0 w-full md:w-[60%] lg:w-[56%] pointer-events-none overflow-hidden">
          <img src="/images/generated/pc-hero.png" alt="Чистка комп'ютера" className="w-full h-full object-contain object-right" data-testid="pc-hero-img" />
          <div className="absolute inset-y-0 left-0 w-1/2 console-hero-fade" />
        </div>

        <div className="container mx-auto max-w-7xl px-4 relative z-10">
          <div className="flex flex-col items-start mb-8">
            <Link to="/" className="inline-flex items-center gap-2 hero-back-link mb-6 transition-colors text-sm" data-testid="back-home-link">
              <ArrowLeft className="w-4 h-4" /> На головну
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/30 mb-5 backdrop-blur-sm">
              <MonitorSmartphone className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-xs uppercase tracking-widest text-orange-400">Чистка комп'ютерів · Одеса</span>
            </div>
          </div>

          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.02] mb-5 hero-h1">
              Чистка <span className={GRAD}>комп'ютера</span>
              <br />в Одесі
            </h1>
            <p className="text-lg md:text-xl mb-6 max-w-xl hero-lead">
              Ваш ПК перестане перегріватися та шуміти. Продуваємо пил, міняємо термопасту, обслуговуємо кулери — температури нижчі в середньому на <span className="font-bold hero-lead-strong">−15°C</span>. Гарантія <span className="font-bold hero-lead-strong">30 днів</span>.
            </p>

            <div className="flex flex-wrap items-baseline gap-3 mb-8">
              <div className="hero-price-from text-sm">від</div>
              <div className={`text-6xl md:text-7xl font-black ${GRAD}`}>500</div>
              <div className="hero-price-unit text-2xl font-bold">грн</div>
            </div>

            <div className="flex flex-wrap gap-3">
              <a href="tel:+380974178788" data-testid="hero-cta-call" className={`inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r ${ACCENT} text-white font-bold rounded-full transition-all duration-300 hover:scale-105 ${GLOW}`}>
                <Phone className="w-5 h-5" /> Записатись зараз
              </a>
              <a href="#steps" data-testid="hero-cta-details" className="inline-flex items-center gap-2 px-8 py-4 hero-cta-secondary font-semibold rounded-full border transition-all">
                Як ми це робимо <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="flex flex-wrap gap-6 mt-8 text-sm hero-meta">
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-orange-400" /> 1–2 години</div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-orange-400" /> Гарантія 30 днів</div>
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-orange-400" /> Діагностика 0 грн</div>
            </div>
          </div>

          <div className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 z-10 hidden lg:block">
            <div className="glass-card px-5 py-4 backdrop-blur-2xl border text-center rotate-3 hero-effect-badge">
              <div className="text-[10px] uppercase tracking-widest hero-effect-label">Ефект</div>
              <div className={`text-4xl font-black ${GRAD}`}>−15°C</div>
              <div className="text-xs hero-effect-sub mt-1">після чистки</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="glass-card p-6 md:p-10 backdrop-blur-2xl bg-white/[0.03] border border-white/10">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-1">
                <div className="text-xs uppercase tracking-widest text-orange-400 mb-2">Що входить</div>
                <h3 className="text-2xl md:text-3xl font-black text-white leading-tight">Повний профілактичний комплекс для ПК</h3>
                <p className="text-gray-400 text-sm mt-3">Все включено у ціну. Ніяких прихованих доплат.</p>
              </div>
              <ul className="md:col-span-2 grid sm:grid-cols-2 gap-3">
                {FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-orange-400" />
                    <span className="text-sm md:text-base">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Signs */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-widest text-orange-400 mb-2">Симптоми</div>
            <h2 className="text-3xl md:text-5xl font-black text-white">Коли ПК пора на <span className={GRAD}>чистку</span></h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {SIGNS.map((s, i) => (
              <div key={i} className="glass-card p-5 hover:border-orange-500/50 transition-all" data-testid={`sign-${i}`}>
                <div className="text-3xl mb-2">{s.icon}</div>
                <p className="text-gray-300 text-sm md:text-base">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section id="steps" className="py-14 md:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-widest text-orange-400 mb-2">Процес</div>
            <h2 className="text-3xl md:text-5xl font-black text-white mb-4">Як проходить <span className={GRAD}>чистка</span></h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Прозоро від першого дзвінка до тестування. Фотографуємо кожен етап і надсилаємо вам у Telegram.</p>
          </div>
          <div className="space-y-8 md:space-y-16">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const reverse = i % 2 === 1;
              return (
                <div key={i} className={`grid md:grid-cols-2 gap-6 md:gap-10 items-center ${reverse ? 'md:[direction:rtl]' : ''}`} data-testid={`step-${i}`}>
                  <div className="md:[direction:ltr]">
                    <div className="rounded-2xl overflow-hidden border border-white/10 relative aspect-[4/3] bg-black/40">
                      <img src={s.img} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute top-3 left-3 w-10 h-10 rounded-full bg-black/70 backdrop-blur flex items-center justify-center text-orange-400 font-black text-lg border border-orange-500/40">
                        {String(i + 1).padStart(2, '0')}
                      </div>
                    </div>
                  </div>
                  <div className="md:[direction:ltr]">
                    <div className={`inline-flex items-center gap-2 mb-3 px-3 py-1.5 rounded-full bg-gradient-to-r ${ACCENT}`}>
                      <Icon className="w-4 h-4 text-white" />
                      <span className="text-xs uppercase tracking-wider text-white font-semibold">Крок {i + 1}</span>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-black text-white mb-3">{s.title}</h3>
                    <p className="text-gray-300 text-base md:text-lg leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Price + trust */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-6xl grid md:grid-cols-2 gap-8 items-stretch">
          <div className="glass-card p-8 flex flex-col justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-orange-400 mb-3">Прайс</div>
              <h3 className="text-3xl font-black text-white mb-6">Ціна на чистку ПК</h3>
              <div className="flex items-baseline gap-3 mb-6">
                <div className="text-gray-400">від</div>
                <div className={`text-7xl font-black ${GRAD}`}>500</div>
                <div className="text-gray-400 text-2xl">грн</div>
              </div>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" /> Чистка від пилу входить у ціну</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" /> Термопаста преміум-класу — у вартості</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" /> Обслуговування відеокарти — за потреби</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" /> Діагностика — 0 грн, гарантія 30 днів</li>
              </ul>
            </div>
            <a href="tel:+380974178788" data-testid="price-cta-call" className={`mt-8 inline-flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r ${ACCENT} text-white font-bold rounded-full text-lg ${GLOW}`}>
              <Phone className="w-5 h-5" /> Записатись за 30 сек
            </a>
          </div>
          <div className="glass-card p-8 space-y-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-orange-400 mb-3">Чому нас</div>
              <h3 className="text-3xl font-black text-white">Обирають в Одесі</h3>
            </div>
            <div className="space-y-4">
              {[
                { n: '8+', l: 'років обслуговуємо комп\'ютери' },
                { n: '4000+', l: 'ПК пройшло через наші руки' },
                { n: '−15°C', l: 'середнє зниження температури' },
                { n: '30 днів', l: 'офіційної гарантії' }
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className={`text-3xl md:text-4xl font-black ${GRAD} min-w-[100px]`}>{s.n}</div>
                  <div className="text-gray-300">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <InstagramVideoSection
        url={landing?.instagram_video}
        accentClass="text-orange-400"
      />

      {/* FAQ */}
      <section className="py-14 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-widest text-orange-400 mb-2">FAQ</div>
            <h2 className="text-3xl md:text-5xl font-black text-white">Часті питання</h2>
          </div>
          <div className="space-y-3" data-testid="faq-section">
            {FAQS.map((f, i) => (
              <details key={i} className="glass-card p-6 group cursor-pointer" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between text-white font-semibold list-none">
                  <span className="pr-4">{f.q}</span>
                  <span className="text-orange-400 text-2xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-gray-400 mt-4 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 md:py-24 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="glass-card p-8 md:p-14 text-center relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4">Ваш ПК заслуговує <span className={GRAD}>прохолоди</span></h2>
              <p className="text-gray-300 text-lg mb-8 max-w-xl mx-auto">Один дзвінок — і вже сьогодні ви заберете комп'ютер, який працює тихо, швидко й без перегріву.</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <a href="tel:+380974178788" data-testid="final-cta-call" className={`inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r ${ACCENT} text-white font-bold rounded-full text-lg ${GLOW}`}>
                  <Phone className="w-5 h-5" /> +38 097 417 8788
                </a>
                <a href="https://t.me/cimmodua" target="_blank" rel="noopener noreferrer" data-testid="final-cta-telegram" className="inline-flex items-center gap-2 px-10 py-4 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-full border border-white/10 transition-all">
                  <Send className="w-5 h-5" /> Написати в Telegram
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PCCleaningPage;
